/**
 * What a room is called once its floor has been merged into another survey.
 *
 * Merging two walks into one property (migration 0069) MOVES the source
 * survey's rooms onto a new storey of the target, and namespaces their
 * identifiers by the floor they became: `L1:<ref>`. It has to. Every phone
 * walk numbers its own rooms from the start, so two walks of one house arrive
 * with the same identifiers, and without the prefix the upstairs lounge would
 * read as an edit to the downstairs one.
 *
 * THE COST OF THAT PREFIX IS THIS FILE. A capture still calls the room by its
 * bare ref — it knows nothing of the merge — so anything matching plan
 * geometry to a survey's rooms by identity misses an entire merged floor. It
 * has caused the same bug three times in different places: the design check
 * reported every upstairs room as having no heat loss, the plan drew them with
 * the scan's own labels instead of the surveyor's names, and on the portal the
 * whole upstairs could not be answered for party walls at all.
 *
 * So the rule lives HERE, in the package both the API and the portal already
 * import, rather than being written out a fourth time.
 */

/** How a merged floor's room identifier is built. `L` then the level, then a colon. */
export function mergedRoomRef(level: number, captureRef: string): string {
  return `L${level}:${captureRef}`;
}

/**
 * What the DATABASE calls the room a capture calls `captureRef`, on `level`.
 *
 * Decided from what the survey ACTUALLY HOLDS rather than from the prefix
 * rule: `known` is the set of identifiers on the survey's own rooms. So a
 * floor that was never namespaced, a level 0 which never is, and any later
 * change to how a merge names things all come out right — and a ref that
 * belongs to no room is returned unchanged rather than invented into one.
 */
export function dbRoomRef(
  level: number,
  captureRef: string,
  known: ReadonlySet<string>,
): string {
  const namespaced = mergedRoomRef(level, captureRef);
  return known.has(namespaced) ? namespaced : captureRef;
}
